import React, { useState, useRef } from "react";
import Papa from "papaparse";
import toast from "react-hot-toast";
import {
  X,
  FileUp,
  Download,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  Loader2,
  TableProperties,
} from "lucide-react";
import api from "../../connector/api";

export default function BulkUploadModal({ onClose, onUploadComplete }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const REQUIRED_COLUMNS = [
    "Title", "Author", "Category", "ISBN", "Barcode", 
    "Description", "Call Number", "Publication Year", "Publisher", "Status"
  ];

  const TEMPLATE_CSV = `Title,Author,Category,ISBN,Barcode,Description,Call Number,Publication Year,Publisher,Status
The Great Gatsby,F. Scott Fitzgerald,Classic,9780743273565,1234567890123,A novel set in the 1920s,PS3511.I9 G7,1925,Scribner,Available
Atomic Habits,James Clear,Self-Help,9780735211292,1234567890124,Build good habits and break bad ones,BF637.S8 C54,2018,Random House,Available`;

  const handleTemplateDownload = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "cais_bulk_upload_template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const processFile = (selectedFile) => {
    if (!selectedFile) return;
    if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith(".csv")) {
      setErrors(["Unsupported file layout type. Please map your assets using a valid .csv framework."]);
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    setPreview([]);

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const missing = REQUIRED_COLUMNS.filter(
          (col) => !results.meta.fields?.includes(col)
        );
        if (missing.length > 0) {
          return setErrors([`Missing mandatory column definitions: ${missing.join(", ")}`]);
        }
        setPreview(results.data.slice(0, 5));
      },
      error: (err) => setErrors([`Parsing structural exception: ${err.message}`]),
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setIsDragActive(true);
    else if (e.type === "dragleave") setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please select a file to execute");
    if (errors.length > 0) return toast.error("Please resolve parsing exceptions first");

    setUploading(true);
    setProgress(0);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;
        const total = rows.length;
        let createdCount = 0;

        try {
          const batchSize = 10;
          const totalBatches = Math.ceil(total / batchSize);

          for (let i = 0; i < totalBatches; i++) {
            const batch = rows.slice(i * batchSize, (i + 1) * batchSize);
            const csvWithHeaders = Papa.unparse(batch, { header: true });

            const formData = new FormData();
            formData.append(
              "file",
              new Blob([csvWithHeaders], { type: "text/csv" })
            );

            const res = await api.post("/cataloging/bulk_upload/", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });

            createdCount += res.data.created_books?.length || 0;
            setProgress(Math.round(((i + 1) / totalBatches) * 100));
          }

          toast.success(`Successfully registered ${createdCount} metadata files into inventory ledger.`);
          onUploadComplete?.();
          onClose();
        } catch (err) {
          toast.error("Manifest ingest transaction processing error");
        } finally {
          setUploading(false);
          setProgress(0);
        }
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs px-4 animate-fade-in">
      <div className="bg-[#131110] text-stone-200 rounded-2xl border border-[#2e2720] shadow-2xl w-full max-w-2xl mx-auto flex flex-col max-h-[85vh] overflow-hidden">
        
        {/* HEADER BLOCK */}
        <div className="p-4 sm:p-5 border-b border-[#2e2720] flex items-center justify-between bg-[#1a1613] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center">
              <FileUp className="w-4 h-4 stroke-[2]" />
            </div>
            <div className="space-y-0.5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-md w-max">
                Batch Pipeline
              </div>
              <h2 className="text-sm font-sans font-bold tracking-tight text-stone-100">
                Bulk Cataloging Management
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="p-1.5 text-stone-400 hover:text-stone-100 hover:bg-[#25201b] rounded-xl transition-colors focus:outline-none"
            onClick={onClose}
          >
            <X className="w-4 h-4 stroke-[2.5]" />
          </button>
        </div>

        {/* INNER SCROLLABLE WORKSPACE */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5 text-xs text-stone-300 font-medium leading-relaxed custom-scrollbar">
          
          {/* SYSTEM GUIDELINE CALLOUT */}
          <div className="bg-[#1a1613] border border-[#2e2720] p-4 rounded-xl space-y-2">
            <h3 className="font-bold text-stone-400 text-[10px] uppercase tracking-wider">
              Manifest Constraints
            </h3>
            <p className="text-stone-400 text-xs">
              Your source file header layout must strictly map to the schema properties listed below. The platform auto-resolves covers via standard Google API handshakes from verified ISBN targets.
            </p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {REQUIRED_COLUMNS.map((col) => (
                <span
                  key={col}
                  className="px-2 py-0.5 bg-[#131110] border border-[#2e2720] text-amber-400/90 text-[10px] font-bold rounded-md font-mono"
                >
                  {col}
                </span>
              ))}
            </div>
          </div>

          {/* TEMPLATE DOWNLOAD CONSOLE DECK */}
          <div className="flex items-center justify-between bg-[#1a1613] border border-[#2e2720] p-3.5 rounded-xl gap-3">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <h4 className="font-bold text-stone-200 text-xs">Require a clean schematic format?</h4>
                <p className="text-stone-500 text-[10px]">Download our predefined boilerplate structural array layout mapping.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleTemplateDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#131110] border border-[#2e2720] text-stone-200 hover:text-amber-400 hover:bg-[#201c17] hover:border-amber-500/30 transition-all font-bold text-[11px] rounded-xl shrink-0 active:scale-95"
            >
              <Download className="w-3.5 h-3.5" />
              Template.CSV
            </button>
          </div>

          {/* DRAG AND DROP SHELF */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center group ${
                isDragActive
                  ? "border-amber-500 bg-amber-500/10"
                  : file
                  ? "border-emerald-500/60 bg-emerald-500/5"
                  : "border-[#2e2720] bg-[#1a1613]/50 hover:bg-[#1a1613] hover:border-amber-500/40"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={(e) => processFile(e.target.files?.[0])}
                className="hidden"
              />
              
              {file ? (
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2 stroke-[1.8]" />
              ) : (
                <FileUp className="w-8 h-8 text-stone-500 group-hover:text-amber-400 transition-colors mb-2 stroke-[1.8]" />
              )}

              <p className="font-bold text-stone-200 text-xs">
                {file ? file.name : "Drag and drop your spreadsheet layout here"}
              </p>
              <p className="text-[10px] text-stone-500 mt-0.5">
                {file
                  ? `Size metrics: ${(file.size / 1024).toFixed(1)} KB`
                  : "or click manually to open local storage nodes (CSV file maximum)"}
              </p>
            </div>

            {/* ERROR PRESENTATION STRIP */}
            {errors.length > 0 && (
              <div className="p-3.5 bg-rose-950/30 border border-rose-800/40 text-rose-300 rounded-xl flex items-start gap-2.5">
                <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <h5 className="font-bold text-[10px] uppercase tracking-wider text-rose-400">
                    Validation Exception
                  </h5>
                  {errors.map((err, i) => (
                    <p key={i} className="text-xs font-medium leading-normal text-rose-200">
                      {err}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* LIVE SAMPLE INTERACTIVE PREVIEW GRID */}
            {preview.length > 0 && errors.length === 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-stone-400 font-bold uppercase text-[10px] tracking-wider pl-1">
                  <TableProperties className="w-3.5 h-3.5 text-amber-400" />
                  <span>Staging Data Sample Grid (First 5 Items)</span>
                </div>
                <div className="border border-[#2e2720] rounded-xl overflow-hidden max-h-44 overflow-y-auto bg-[#131110] custom-scrollbar">
                  <table className="w-full text-[11px] border-collapse text-left whitespace-nowrap">
                    <thead className="bg-[#1a1613] border-b border-[#2e2720] font-bold text-stone-400 sticky top-0 z-10">
                      <tr>
                        {REQUIRED_COLUMNS.map((col) => (
                          <th
                            key={col}
                            className="p-2.5 font-bold text-stone-300 border-r border-[#2e2720] last:border-0 uppercase text-[9px] tracking-wider"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2e2720]/60 text-stone-300 font-medium">
                      {preview.map((row, idx) => (
                        <tr key={idx} className="hover:bg-[#1a1613] transition-colors">
                          {REQUIRED_COLUMNS.map((col) => (
                            <td
                              key={col}
                              className="p-2.5 max-w-xs truncate border-r border-[#2e2720]/40 last:border-0 text-stone-200"
                            >
                              {row[col] || "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ACTIVE INGESTION STEPPING BAR */}
            {uploading && (
              <div className="space-y-2 bg-[#1a1613] border border-[#2e2720] p-4 rounded-xl">
                <div className="flex justify-between text-xs font-bold text-stone-200">
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />
                    Committing dataset rows into secure databases...
                  </span>
                  <span className="font-mono text-amber-400 font-bold">{progress}%</span>
                </div>
                <div className="w-full bg-[#131110] rounded-full h-2 overflow-hidden border border-[#2e2720]">
                  <div
                    className="bg-amber-500 h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* TRANSACTION ACTION CONTROLS FOOTER */}
            <div className="flex items-center justify-end gap-3 border-t border-[#2e2720] pt-4 mt-6">
              <button
                type="button"
                onClick={onClose}
                disabled={uploading}
                className="px-4 py-2 border font-mono border-[#2e2720] rounded-xl font-bold text-[11px] uppercase tracking-wider text-stone-400 hover:bg-[#201c17] transition active:scale-95 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading || !file || errors.length > 0}
                className="flex items-center font-mono justify-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-[11px] uppercase tracking-wider rounded-xl shadow transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Ingesting Payload...
                  </>
                ) : (
                  "Execute Bulk Import"
                )}
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}